import { useState, useEffect } from "react";
import { Box, Stack, Container, Grid, Typography, Card, ButtonBaseProps } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Link } from "react-router-dom";
import axios from "axios";

import addCount from "Helper/addCount";

import PhoneImage from "Assets/Home/phone.png";
import LogoMark from "Assets/logo-mark.svg";

import CheckService from "Components/CheckService";

import { API_URL } from "Constants";
import { toast } from "react-toastify";

interface ICustomButtonProps extends ButtonBaseProps {
  background?: string;
  color?: string;
  link?: string;
  loading?: boolean;
  isRoute?: boolean;
}

const BUTTONS = [
  {
    text: "403 چیست؟",
    link: "#about-us",
    grid: 2.5,
  },
  {
    text: "سرویس‌های تحت پوشش",
    link: "/services",
    grid: 4.5,
    isRoute: true,
  },
  {
    text: "اپلیکیشن و دی‌ان‌اس",
    link: "/download",
    grid: 3.5,
    isRoute: true,
  },
];
const WHITE_GRADIENT = "linear-gradient(180deg, rgba(255, 255, 255, 1), rgba(200, 200, 200, 1))";

const CustomButton = (props: ICustomButtonProps) => {
  const { children, background = WHITE_GRADIENT, color = "primary.main", link, onClick, loading, isRoute } = props;

  const Button = (
    <LoadingButton
      loading={loading}
      onClick={onClick}
      variant="contained"
      sx={{
        borderRadius: "100px",
        width: "100%",
        height: "60px",
        fontSize: "18px",
        fontWeight: "600",
        color,
        background: background,
      }}
    >
      {children}
    </LoadingButton>
  );

  return link ? <>{isRoute ? <Link to={link}>{Button}</Link> : <a href={link}>{Button}</a>}</> : Button;
};

export default function Home() {
  const [supportStatus, setSupportStatus] = useState(null);
  const [showSupportStatus, setShowSupportStatus] = useState<boolean>(false);
  const [searchFilterLoading, setSearchFilterLoading] = useState<boolean>(false);
  const [searchFilterInput, setSearchFilterInput] = useState<string>("");

  const checkService = () => {
    setSearchFilterLoading(true);
    axios
      .get(`${API_URL}/search-filter?url=${searchFilterInput}`)
      .then((res) => {
        if (res.data.isSuccess === false) {
          toast.error("لطفا ساختار صحیح دامنه را وارد کنید");
          setSearchFilterLoading(false);
        } else {
          setSupportStatus(res.data.result);
          setSearchFilterLoading(false);
          setShowSupportStatus(true);
        }
      })
      .catch((err) => {
        toast.error("تعداد درخواست‌های شما بیش از حد مجاز است!");
        setSearchFilterLoading(false);
      });
  };

  useEffect(() => {
    addCount("home");
  }, []);

  return (
    <>
      <CheckService isOpen={showSupportStatus} onClose={() => setShowSupportStatus(false)} status={supportStatus} />

      <Box
        sx={{
          bgcolor: "#fff",
          backgroundImage: "url(/assets/background-shape.svg)",
          backgroundPosition: "center",
          backgroundSize: "cover",
          minHeight: "100vh",
        }}
      >
        <Container maxWidth="xl" sx={{ pt: { xs: "140px", lg: "200px" } }}>
          <Grid container direction={{ xs: "column-reverse", lg: "row" }} columnSpacing={10} rowGap={5} alignItems="center">
            <Grid item xs={12} lg={4} sx={{ "& img": { maxWidth: { xs: "200px", lg: "600px" } } }}>
              <img src={PhoneImage} style={{ width: "100%" }} />
            </Grid>

            <Grid item xs={12} lg={8}>
              <Stack spacing={3} alignItems={{ xs: "center", lg: "flex-start" }}>
                <Typography variant="h1" fontWeight={800} sx={{ fontSize: { xs: "35px", md: "68px" } }}>
                  آزاد از تحریم‌های آنلاین
                </Typography>
                <Typography variant="h1" fontWeight={800} sx={{ fontSize: { xs: "30px", md: "68px" } }}>
                  با سرویس رفع تحریم 403
                </Typography>
              </Stack>

              <Grid container mt={4} columnSpacing={{ xs: 1, xl: 3 }} rowGap={1}>
                {BUTTONS.map((button) => (
                  <Grid item xs={12} lg={button.grid}>
                    <CustomButton link={button.link} isRoute={button.isRoute || false}>
                      {button.text}
                    </CustomButton>
                  </Grid>
                ))}
              </Grid>

              <Grid container mt={4} columnSpacing={{ xs: 1, xl: 3 }}>
                <Grid item xs={8} md={10}>
                  <Card
                    sx={{
                      px: 3,
                      borderRadius: "100px",
                      flexGrow: 1,
                      background: WHITE_GRADIENT,
                      "& input": {
                        fontSize: { xs: "15px", md: "18px" },
                        direction: "rtl",
                        "&::placeholder": { textAlign: "left" },
                      },
                    }}
                  >
                    <input
                      type="text"
                      value={searchFilterInput}
                      onChange={(e) => setSearchFilterInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") checkService();
                      }}
                      placeholder="بررسی تحریم بودن دامنه"
                      style={{
                        background: "transparent",
                        border: "none",
                        outline: "none",
                        color: "#333",
                        fontFamily: "Kalameh",
                        fontWeight: "",
                        width: "100%",
                        height: "60px",
                      }}
                    />
                  </Card>
                </Grid>

                <Grid item xs={4} md={2}>
                  <CustomButton
                    background="linear-gradient(180deg, rgba(154, 190, 255, 1), rgba(48, 121, 255, 1))"
                    color="#fff"
                    onClick={checkService}
                    loading={searchFilterLoading}
                  >
                    بررسی
                  </CustomButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ bgcolor: "#fff", pt: 10, pb: 4 }} id="about-us">
        <Container maxWidth="xl">
          <Grid container alignItems="center" columnSpacing={10} rowGap={5}>
            <Grid item xs={12} md={10}>
              <Stack spacing={2}>
                <Typography variant="h3" sx={{ color: "primary.main", fontWeight: "700" }}>
                  403 چیست
                </Typography>

                <Typography
                  sx={{
                    fontSize: "20px",
                    textAlign: "justify",
                    lineHeight: "40px",
                    color: "text.primary",
                    fontWeight: "600",
                    wordSpacing: "-1px",
                  }}
                >
                  ۴۰۳ پلتفرمی برای برنامه‌نویسان و توسعه‌دهندگان عزیز کشورمان است که امروزه با انواع تحریم و اختلال در توسعه
                  پروژه‌های مورد نظرشان مواجه هستند. این پروژه با پشتیبانی از پروتکل‌های مختلف به کاربران این امکان را می‌دهد که
                  با حذف مشکلات موجود به کتابخانه‌ها و وبسایت‌هایی که برای توسعه نیاز دارند دسترسی داشته باشند. این سایت به مرور
                  زمان با بازخورد کاربران تکمیل می‌شود تا تمام مشکلات این جامعه گرانقدر را رفع کند.
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={2}>
              <img src={LogoMark} alt="logo" style={{ width: "100%", maxWidth: "200px", margin: "0 auto" }} />
            </Grid>
          </Grid>
        </Container>

        <Stack alignItems="center" mt={10} spacing={2}>
          <Typography sx={{ color: "primary.main", fontWeight: 600, textAlign: "center" }}>
            تمامی حقوق این وبسایت برای 403 محفوظ است و هرگونه کپی برداری پیگرد قانونی خواهد داشت
          </Typography>

          <Stack direction="row" alignItems="center" spacing={2} sx={{ width: "100%", px: 5 }}>
            <Box sx={{ flexGrow: 1, height: "3px", bgcolor: "primary.main" }} />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ color: "primary.main" }}>
              <img src={LogoMark} alt="logo" style={{ width: "30px" }} />
            </Stack>
            <Box sx={{ flexGrow: 1, height: "3px", bgcolor: "primary.main" }} />
          </Stack>
        </Stack>
      </Box>
    </>
  );
}
